import React, { useContext, useEffect } from "react";
import parse from "html-react-parser";
import propTypes from "prop-types";
import { startCase } from "lodash";

import { api, KeyAuditItemStatus, VehicleAuditItemStatus, VehicleAuditIssueType, VehicleAudit, KeyAudit, KeyAuditItem, VehicleAuditItem } from "../../../../logic/api";

import flex from "../../../../styles/flex";
import styles from "./styles";

import { SessionContext } from "../../../../utility/useSession";
import { useDate } from "../../../../utility/useDate";

import { ProgressView } from "../../../../components/notifications/progress/ProgressView";
import { useTranslation } from "react-i18next";

interface AuditHistoryItem {
    auditDate: string,
    description: string
}

const AuditHistoryTable: React.FunctionComponent<{
    bookingId: number,
}> = (props) => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const { toFormattedDateTimeString } = useDate();

    const getAuditHistory = api.hooks.booking.getAuditHistory();

    useEffect(() => {
        getAuditHistory.send(session.current.context, {
            bookingId: props.bookingId
        });
    }, [props.bookingId]);

    const auditHistory = getAuditHistory.data;

    const isKeyAudit = (audit: KeyAudit | VehicleAudit): audit is KeyAudit => (Object.hasOwn(audit, "keyAuditItems"));
    const isVehicleAudit = (audit: KeyAudit | VehicleAudit): audit is VehicleAudit => (Object.hasOwn(audit, "vehicleAuditItems"));

    const getIssuesList = (issues: VehicleAuditIssueType[]) => {
        return `(<b>${issues.map(issue => startCase(VehicleAuditIssueType[issue])).join(", ")}</b>)`
    }

    const getAuditHistoryItems = (audit: KeyAudit | VehicleAudit, auditor: string, auditDate: string) => {
        const auditType = isKeyAudit(audit) ? "Key" : "Vehicle";
        const auditItems = isKeyAudit(audit) ? audit.keyAuditItems : audit.vehicleAuditItems;
        const auditItemStatus = (item: KeyAuditItem | VehicleAuditItem) => isKeyAudit(audit)
            ? startCase(KeyAuditItemStatus[item.status])
            : startCase(VehicleAuditItemStatus[item.status]);

        if (!auditItems) {
            return [];
        }

        return auditItems.map(item => {
            if ((isKeyAudit(audit) && item.status === KeyAuditItemStatus.missing) || (isVehicleAudit(audit) && item.status === VehicleAuditItemStatus.missing)) {
                return { description: `${auditType} recorded as <b>MISSING</b> from location <b>${item.expectedLocation?.name || 'unknown'}</b> by <b>${auditor}</b> during lane audit`, auditDate };
            }

            return { description: `${auditType} location <b>${audit.location?.parentName}, ${audit.location?.name}</b> recorded by <b>${auditor}</b> during lane audit (<b>${auditItemStatus(item)}</b>)`, auditDate };
        });
    }

    const getKeyAuditItemHistory = (audit: KeyAudit, auditor: string, auditDate: string) => {
        if (!audit.keyAuditItems?.length) {
            return [];
        }

        return getAuditHistoryItems(audit, auditor, auditDate);
    }

    const getVehicleAuditHistoryItems = (audit: VehicleAudit, auditor: string, auditDate: string) => {
        const auditItemHistory = getVehicleAuditItemHistory(audit, auditor, auditDate);
        const auditIssueHistory = getVehicleAuditIssueHistory(audit, auditor, auditDate);
        const auditIssuePhotoHistory = getVehicleAuditIssuePhotoHistory(audit, auditor, auditDate);

        return [...auditItemHistory, ...auditIssueHistory, ...auditIssuePhotoHistory];
    }

    const getVehicleAuditItemHistory = (audit: VehicleAudit, auditor: string, auditDate: string) => {
        if (!audit.vehicleAuditItems?.length) {
            return [];
        }

        return getAuditHistoryItems(audit, auditor, auditDate);
    }

    const getVehicleAuditIssueHistory = (audit: VehicleAudit, auditor: string, auditDate: string) => {
        if (!audit.vehicleAuditIssues?.length) {
            return [];
        }

        const issues: number[] = audit.vehicleAuditIssues.map(vai => vai.issueType);

        if (!issues.length) {
            return [];
        }

        return [{ description: `<b>${audit.vehicleAuditIssues.length} issue${audit.vehicleAuditIssues.length === 1 ? '' : 's'}</b> recorded by <b>${auditor}</b> during lane audit ${getIssuesList(issues)}`, auditDate }];
    }

    const getVehicleAuditIssuePhotoHistory = (audit: VehicleAudit, auditor: string, auditDate: string) => {
        if (!audit.vehicleAuditIssuePhotos?.length) {
            return [];
        }

        return [{ description: `${audit.vehicleAuditIssuePhotos.length} photo${audit.vehicleAuditIssuePhotos.length === 1 ? '' : 's'} added by <b>${auditor}</b> during lane audit`, auditDate }]
    }

    const getAuditItems = () => {
        if (!auditHistory) {
            return [];
        }

        const auditHistoryItems = auditHistory?.reduce<AuditHistoryItem[]>((accumulator, audit) => {
            const auditor = `${audit.createdBy.firstName} ${audit.createdBy.lastName}`;

            const auditDate = toFormattedDateTimeString(audit.createDate);

            if (isKeyAudit(audit)) {
                return [...accumulator, ...getKeyAuditItemHistory(audit, auditor, auditDate)];
            }

            if (isVehicleAudit(audit)) {
                return [...accumulator, ...getVehicleAuditHistoryItems(audit, auditor, auditDate)];
            }

            return accumulator;
        }, [] as AuditHistoryItem[]);

        return auditHistoryItems;
    }

    return <ProgressView status={getAuditHistory.status}>
        {
            auditHistory?.length === 0 ? (
                <div>{t("input.validations.noAuditHistory")}</div>
            ) : (
                <div>
                    {getAuditItems().map((historyItem: AuditHistoryItem, index) => {
                        return <div key={`auditHistory-${index}`} style={flex.flexRows}>
                            <div style={flex.flexAuto}>
                                <div data-testid={`auditHistoryItemDate-${index}`} style={styles.cell}>
                                    {parse(historyItem.auditDate)}
                                </div>
                            </div>
                            <div style={flex.flexFill}>
                                <div data-testid={`auditHistoryItemDescription-${index}`} style={styles.cell}>
                                    {parse(historyItem.description)}
                                </div>
                            </div>
                        </div>
                    })}
                </div>
            )
        }

    </ProgressView>
}

AuditHistoryTable.propTypes = {
    bookingId: propTypes.number.isRequired,
}

export default AuditHistoryTable;